import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const KEY = "nv_analytics_consent_v1";

function savedChoice() {
  try {
    const value = window.localStorage.getItem(KEY);
    return value === "accepted" || value === "rejected" ? value : null;
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const [choice, setChoice] = useState(savedChoice);
  const [open, setOpen] = useState(() => !savedChoice());
  const [customizing, setCustomizing] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(
    () => savedChoice() === "accepted"
  );

  useEffect(() => {
    const showPreferences = () => {
      setAnalyticsEnabled(choice === "accepted");
      setCustomizing(false);
      setOpen(true);
    };
    window.addEventListener("nv:open-cookie-preferences", showPreferences);
    return () =>
      window.removeEventListener("nv:open-cookie-preferences", showPreferences);
  }, [choice]);

  const select = (nextChoice) => {
    if (nextChoice === "rejected" && window.posthog?.opt_out_capturing) {
      window.posthog.opt_out_capturing();
    }
    try {
      window.localStorage.setItem(KEY, nextChoice);
    } catch {
      // The choice still applies to this page when storage is unavailable.
    }
    setChoice(nextChoice);
    setCustomizing(false);
    setOpen(false);
    if (nextChoice === "accepted" || choice === "accepted") {
      window.location.reload();
    }
  };

  if (!open) return null;

  return (
    <section
      role="region"
      aria-labelledby="cookie-settings-title"
      className="fixed inset-x-0 bottom-0 z-[100] border-t-[3px] border-t-[#D4A03A] bg-[#231F1E] px-4 py-3 text-[#FAF3E7] shadow-[0_-8px_30px_rgba(28,20,18,0.25)] sm:px-6"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
        <div className="min-w-0 lg:max-w-2xl">
          <h2 id="cookie-settings-title" className="font-serif text-base font-semibold">
            Cookie settings
          </h2>
          <p className="mt-1 text-xs leading-5 text-[#E0D4CA] sm:text-sm">
            Essential cookies keep shopping and sign-in working. Optional cookies help us
            understand site activity. Read our{" "}
            <Link to="/cookie-policy" className="font-semibold text-[#E4BC79] underline underline-offset-2 hover:text-white">
              Cookie Policy
            </Link>.
          </p>
        </div>
        <div className="grid shrink-0 grid-cols-2 gap-2 sm:grid-cols-3 lg:w-auto">
          <button
            type="button"
            aria-expanded={customizing}
            aria-controls={customizing ? "cookie-customization" : undefined}
            onClick={() => setCustomizing((value) => !value)}
            className="col-span-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-medium text-[#FAF3E7] hover:bg-white/15 sm:col-span-1 sm:text-sm"
          >
            {customizing ? "Close preferences" : "Customize Cookies"}
          </button>
          <button type="button" onClick={() => select("rejected")}
            className="rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-[#FAF3E7] hover:bg-white/15 sm:text-sm">
            Reject optional
          </button>
          <button type="button" onClick={() => select("accepted")}
            className="rounded-lg bg-[#FAF3E7] px-3 py-2 text-xs font-semibold text-[#3F1723] hover:bg-white sm:text-sm">
            Accept cookies
          </button>
        </div>
      </div>

      {customizing && (
        <div id="cookie-customization" className="mx-auto mt-3 grid max-w-7xl gap-3 border-t border-white/15 pt-3 sm:grid-cols-[1fr_1fr_auto] sm:items-center">
          <div className="text-xs">
            <strong className="block font-medium">Essential storage</strong>
            <span className="text-[#C8B8AE]">Required for sign-in and cart · Always on</span>
          </div>
          <label className="flex cursor-pointer items-center justify-between gap-3 text-xs">
            <span>
              <strong className="block font-medium">Optional analytics</strong>
              <span className="text-[#C8B8AE]">Activity and possible session replay</span>
            </span>
            <input
              type="checkbox"
              checked={analyticsEnabled}
              onChange={(event) => setAnalyticsEnabled(event.target.checked)}
              className="h-5 w-5 shrink-0 accent-[#8A2439]"
              aria-label="Allow optional analytics"
            />
          </label>
          <button type="button" onClick={() => select(analyticsEnabled ? "accepted" : "rejected")}
            className="rounded-lg bg-[#7C1F30] px-4 py-2 text-xs font-semibold text-white hover:bg-[#96283E] sm:text-sm">
            Save choices
          </button>
        </div>
      )}
    </section>
  );
}
